import { useSyncExternalStore } from "react";

// ─── Messagerie en pleine page (`/messages`) ───
// La même messagerie que le volet flottant, agrandie : `MessagingDock` en reste le seul
// propriétaire (état, requêtes) et s'affiche dans l'emplacement que la page lui tend.
// En pleine page, l'adresse porte la conversation ouverte.

export const MESSAGES_PATH = "/messages";

/** Ancienne page « Permio AI » : l'adresse redirige vers sa conversation, en pleine page. */
export const ASSISTANT_LEGACY_PATH = "/assistant";

/** Comptes qui ont la messagerie (volet et pleine page). */
export const MESSAGING_ROLES: readonly string[] = [
  "owner",
  "manager",
  "agent",
  "read_only",
  "instructor",
];

/** Comptes qui ont Permio AI : ceux que le serveur de l'assistant accepte. */
export const ASSISTANT_ROLES: readonly string[] = [
  "owner",
  "manager",
  "agent",
  "read_only",
];

/** Conversation ouverte ou créée : de l'équipe, avec un ou des élèves, ou avec Permio AI. */
export type MessagingTarget = {
  source: "team" | "student" | "assistant";
  id: string;
};

/** Permio AI : une seule conversation par compte, épinglée en tête de la liste. */
export const ASSISTANT_TARGET: MessagingTarget = {
  source: "assistant",
  id: "permio-ai",
};

/** Ce que la messagerie montre : une conversation, l'échange à ouvrir avec un élève, ou la liste. */
export type MessagingView = {
  selected?: MessagingTarget | null;
  /** Élève dont la boîte commune (ou l'échange à deux, pour un moniteur) est à ouvrir. */
  arrival?: string | null;
};

/**
 * Adresse de la pleine page. `?conversation=<id>` : conversation avec élève(s), comme l'ancienne
 * page « Messages des élèves » ; `&type=equipe` : conversation entre collègues ; `?eleve=<id>` :
 * échange avec cet élève, remplacé par sa conversation une fois ouverte ; `?type=assistant` :
 * conversation avec Permio AI.
 */
export function messagesAddress({ selected, arrival }: MessagingView = {}) {
  const params = new URLSearchParams();
  if (selected?.source === "assistant") params.set("type", "assistant");
  else if (selected) {
    params.set("conversation", selected.id);
    if (selected.source === "team") params.set("type", "equipe");
  } else if (arrival) params.set("eleve", arrival);
  const query = params.toString();
  return query ? `${MESSAGES_PATH}?${query}` : MESSAGES_PATH;
}

export function parseMessagesAddress(search: string): Required<MessagingView> {
  const params = new URLSearchParams(search);
  if (params.get("type") === "assistant")
    return { selected: ASSISTANT_TARGET, arrival: null };
  const conversation = params.get("conversation");
  if (conversation)
    return {
      selected: {
        source: params.get("type") === "equipe" ? "team" : "student",
        id: conversation,
      },
      arrival: null,
    };
  return { selected: null, arrival: params.get("eleve") };
}

// Emplacement de la pleine page, tendu par `MessagesPage` au volet.
let slot: HTMLElement | null = null;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Référence de l'élément où la messagerie s'affiche en pleine page (`null` au départ de la page). */
export function setMessagingPageSlot(node: HTMLElement | null) {
  if (slot === node) return;
  slot = node;
  listeners.forEach((listener) => listener());
}

export const useMessagingPageSlot = () =>
  useSyncExternalStore(subscribe, () => slot);
